import { ArrowLeftOutlined, DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Card, Col, Form, Image, Input, Modal, Row, Space, message } from 'antd';
import { useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useNavigate, useParams } from 'react-router-dom';
import councilApi from '../../../apis/CouncilApi';
import AddressComponent from '../../../components/AddressComponent/AddressComponent';
import LayoutComponent from '../../../components/LayoutComponent/LayoutComponent';
import useResponsiveLayout from '../../../components/LayoutComponent/UseResponsiveLayout/UseResponsiveLayout';
import CustomContext from '../../../contexts/CustomContext';
import CustomCouncilContext from '../../../contexts/CustomCouncilContext';
import { Council } from '../../../models/Entities';
import banks from '../../../resources/images/banks.svg';
import taxes from '../../../resources/images/taxes.svg';
import users from '../../../resources/images/users.svg';
import alertService from '../../../services/AlertService';
import rolesService from '../../../services/RolesService';
import styles from './CouncilPage.module.scss';

/**
 * Returns the council page.
 * @returns the council page.
 */
const CouncilPage: React.FC = () => {
    /*** HOOKS ***/

    const intl = useIntl();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [desktop] = useResponsiveLayout();
    const params = useParams<ParamsType>();
    const { auth } = useContext(CustomContext);
    const councilContext = useContext(CustomCouncilContext);
    const [loading, setLoading] = useState<'initializing' | 'loading' | 'deleting'>();
    const [council, setCouncil] = useState<Council>();

    /*** EFFECTS ***/

    useEffect(() => {
        const init = async () => {
            try {
                setLoading('initializing');
                // load council
                let council: Council;
                if (councilContext.council && councilContext.council.id) {
                    council = councilContext.council;
                } else {
                    council = {
                        status: 'ENABLED'
                    };
                }
                form.setFieldsValue(council);
                setCouncil(council);
            } catch (error) {
                alertService.displayError(error, intl);
            } finally {
                setLoading(undefined);
            }
        };
        init();
    }, [councilContext.council, form, intl, params.id]);

    /*** METHODS ***/

    const save = async (values: any) => {
        try {
            setLoading('loading');

            let updatedCouncil: Council = Object.assign({}, council, values);
            if (updatedCouncil.id) {
                updatedCouncil = await councilApi.update(updatedCouncil);
            } else {
                updatedCouncil = await councilApi.create(updatedCouncil);
            }
            setCouncil(updatedCouncil);

            message.success(intl.formatMessage({ id: 'status.saved' }));
        } catch (error) {
            alertService.displayError(error, intl);
        } finally {
            setLoading(undefined);
        }
    };

    const remove = async () => {
        Modal.confirm({
            title: intl.formatMessage({ id: 'council.deleteModal.title' }),
            okButtonProps: { loading: loading === 'deleting' },
            onOk: async () => {
                try {
                    setLoading('deleting');

                    await councilApi.delete(council?.id!);
                    message.success(intl.formatMessage({ id: 'status.deleted' }));

                    navigate('/councils');
                } catch (error) {
                    alertService.displayError(error, intl);
                } finally {
                    setLoading(undefined);
                }
            }
        });
    };

    /*** VISUAL ***/

    const isAdmin = rolesService.hasAnyRole(auth, ['ROLE_ADMIN']);

    return (
        <LayoutComponent
            title={<FormattedMessage id="councils.title" />}
            menu="councils"
            path={[
                { path: '/councils', name: <FormattedMessage id="councils.title" /> },
                { path: `/councils/${council?.id}`, name: council?.name }
            ]}
        >
            <Form form={form} onFinish={save} colon={false} layout="vertical">
                <Row gutter={[28, 0]}>
                    <Col span={24}>
                        <Form.Item
                            label={<FormattedMessage id="council.name" />}
                            name="name"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <Input size="large" maxLength={200} />
                        </Form.Item>
                    </Col>
                </Row>
                <CouncilMenu council={council} />
                {isAdmin && (
                    <Row gutter={[28, 0]}>
                        <Col span={24} lg={12}>
                            <Form.Item
                                label={<FormattedMessage id="council.identifier" />}
                                name="identifier"
                                rules={[
                                    { required: true, message: <FormattedMessage id="status.mandatory" /> },
                                    { pattern: /^[P]{1}\d{7}(?:[A-Z0-9])/, message: <FormattedMessage id="status.council.identifier.invalid" /> }
                                ]}
                            >
                                <Input size="large" maxLength={9} />
                            </Form.Item>
                        </Col>
                        <Col span={24} lg={12}>
                            <Form.Item label={<FormattedMessage id="council.iban" />} name="iban">
                                <Input size="large" maxLength={24} />
                            </Form.Item>
                        </Col>
                    </Row>
                )}
                {isAdmin && <AddressComponent field="address" />}
                {isAdmin && (
                    <Row gutter={[28, 0]} className={styles.buttons}>
                        <Col span={24}>
                            <Space>
                                <Button type="primary" htmlType="submit" size="large" loading={loading === 'loading'} icon={<SaveOutlined />}>
                                    <FormattedMessage id="button.save" tagName="span" />
                                </Button>
                                {council && council.id && (
                                    <Button type="primary" danger size="large" onClick={remove} icon={<DeleteOutlined />}>
                                        {desktop && <FormattedMessage id="button.delete" tagName="span" />}
                                    </Button>
                                )}
                                <Link to="/councils">
                                    <Button size="large" icon={<ArrowLeftOutlined />}>
                                        {desktop && <FormattedMessage id="button.back" tagName="span" />}
                                    </Button>
                                </Link>
                            </Space>
                        </Col>
                    </Row>
                )}
            </Form>
        </LayoutComponent>
    );
};
export default CouncilPage;
type ParamsType = { id: string };

/**
 * Returns the council menu.
 * @returns the council menu.
 */
const CouncilMenu: React.FC<CouncilMenuProps> = (props) => {
    const { council } = props;

    return (
        <div className={styles.menu}>
            <Row className={styles.cards} gutter={[28, 28]} justify="center">
                <Col xs={24} md={6}>
                    <Link to={`/councils/${council?.id}/receivables`} className={council && council.id ? undefined : styles.inactive}>
                        <Card className={styles.item} cover={<Image className={styles.image} src={taxes} preview={false} />}>
                            <Card.Meta title={<FormattedMessage id="council.receivables" />} description={<FormattedMessage id="council.receivables.desc" />} />
                        </Card>
                    </Link>
                </Col>
                <Col xs={24} md={6}>
                    <Link to={`/councils/${council?.id}/users`} className={council && council.id ? undefined : styles.inactive}>
                        <Card className={styles.item} cover={<Image className={styles.image} src={users} preview={false} />}>
                            <Card.Meta title={<FormattedMessage id="council.users" />} description={<FormattedMessage id="council.users.desc" />} />
                        </Card>
                    </Link>
                </Col>
                <Col xs={24} md={6}>
                    <Link to={`/councils/${council?.id}/banks`}>
                        <Card className={styles.item} cover={<Image className={styles.image} src={banks} preview={false} />}>
                            <Card.Meta
                                title={<FormattedMessage id="council.bankBranches" />}
                                description={<FormattedMessage id="council.bankBranches.desc" />}
                            />
                        </Card>
                    </Link>
                </Col>
                {/*<Col xs={24} md={6}>
                     <Link to={`/councils/${council?.id}/certificates`} className={council && council.id ? undefined : styles.inactive}>
                        <Card className={styles.item} cover={<Image className={styles.imageCertificates} src={certificates} preview={false} />}>
                            <Card.Meta
                                title={<FormattedMessage id="council.certificates.title" />}
                                description={<FormattedMessage id="council.certificates.desc" />}
                            />
                        </Card>
                    </Link> 
                </Col>*/}
            </Row>
        </div>
    );
};
interface CouncilMenuProps {
    council: Council | undefined;
}
